import { useNavigate, useParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { ChevronRight, RefreshCw, Clock, ShoppingCart } from "lucide-react";
import { RecipeDialog } from "./RecipeDialog";
import { translations } from "@/lib/translations";
import { useState, useEffect, useCallback } from "react";
import {
  getUserMealPlans,
  updateMealInPlan,
  importRecipeFromUrl,
  getFavoriteRecipes,
  addFavoriteRecipe,
  removeFavoriteRecipe,
} from "@/lib/mealPlan";
import { ChangeMealDialog } from "./ChangeMealDialog";
import { MealOptionsDialog } from "./MealOptionsDialog";

export default function MealPlanDisplay() {
  const navigate = useNavigate();
  const { id } = useParams();
  const [mealPlan, setMealPlan] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedMeal, setSelectedMeal] = useState<{
    dayNumber: number;
    type: "breakfast" | "lunch" | "dinner";
  } | null>(null);
  const [showMealOptions, setShowMealOptions] = useState(false);
  const [mealOptions, setMealOptions] = useState([]);
  const [loadingOptions, setLoadingOptions] = useState(false);
  const [favorites, setFavorites] = useState<any[]>([]);

  useEffect(() => {
    loadMealPlan();
    loadFavorites();
    document.documentElement.setAttribute("dir", "rtl");
    document.documentElement.setAttribute("lang", "he");
  }, []);

  const loadMealPlan = async () => {
    try {
      setLoading(true);
      const plans = await getUserMealPlans();
      if (!plans || plans.length === 0) {
        setError("No meal plans found");
        return;
      }

      const selectedPlan = id ? plans.find((plan) => plan.id === id) : plans[0];
      if (selectedPlan) {
        console.log("Loading meal plan:", selectedPlan);
        setMealPlan(selectedPlan.plan_data);
      } else {
        setError("Meal plan not found");
      }
    } catch (err: unknown) {
      const error = err as Error;
      console.error('MealPlanDisplay error:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const loadFavorites = async () => {
    try {
      const data = await getFavoriteRecipes();
      setFavorites(data || []);
    } catch (error) {
      console.error("Error loading favorites:", error);
    }
  };

  const handleChangeMeal = (dayNumber: number, type: "breakfast" | "lunch" | "dinner") => {
    setSelectedMeal({ dayNumber, type });
  };

  const handleImportFromUrl = useCallback(
    async (url: string) => {
      try {
        const importedRecipe = await importRecipeFromUrl(url);
        if (!selectedMeal || !id) return;

        const updatedPlan = await updateMealInPlan(
          selectedMeal.dayNumber,
          selectedMeal.type,
          importedRecipe,
          id,
        );
        setMealPlan(updatedPlan);
        setSelectedMeal(null);
      } catch (error) {
        console.error("Failed to import recipe:", error);
        throw error;
      }
    },
    [selectedMeal, id],
  );

  const handleToggleFavorite = async (meal: any) => {
    try {
      if (isFavorite(meal)) {
        const favoriteToRemove = favorites.find(
          (fav) => fav.recipe.name === meal.name,
        );
        if (favoriteToRemove) {
          await removeFavoriteRecipe(favoriteToRemove.id);
          setFavorites(
            favorites.filter((fav) => fav.id !== favoriteToRemove.id),
          );
        }
      } else {
        await addFavoriteRecipe(meal);
        const updatedFavorites = await getFavoriteRecipes();
        setFavorites(updatedFavorites || []);
      }
    } catch (error) {
      console.error("Error toggling favorite:", error);
    }
  };

  const isFavorite = (meal: any) => {
    return favorites?.some((fav) => fav.recipe.name === meal.name) || false;
  };

  const handleUpdateMeal = async (
    dayNumber: number,
    mealType: string,
    newMeal: any
  ) => {
    try {
      if (!id) return;
      
      const updatedPlan = await updateMealInPlan(
        dayNumber,
        mealType as "breakfast" | "lunch" | "dinner",
        newMeal,
        id
      );

      console.log("Updated plan:", updatedPlan);
      setMealPlan(updatedPlan);
      setShowMealOptions(false);
      setSelectedMeal(null);
    } catch (error) {
      console.error("Failed to update meal:", error);
      // Optionally show error to user
    }
  };

  const handleMealOptionSelect = async (meal: any) => {
    if (!selectedMeal || !id) return;
    await handleUpdateMeal(selectedMeal.dayNumber, selectedMeal.type, meal);
  };

  if (loading) {
    return (
      <div className="w-full min-h-screen bg-gray-50 p-4 md:p-6 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="w-full min-h-screen bg-gray-50 p-4 md:p-6 flex items-center justify-center">
        <p className="text-red-500">{error}</p>
      </div>
    );
  }

  if (!mealPlan) {
    return (
      <div className="w-full min-h-screen bg-gray-50 p-4 md:p-6 flex items-center justify-center">
        <p className="text-muted-foreground">No meal plan found</p>
      </div>
    );
  }

  return (
    <div className="w-full min-h-screen bg-gray-50 p-4 md:p-6">
      <div className="max-w-7xl mx-auto space-y-6">
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={() => navigate("/")}
            className="rtl-button-icon"
          >
            חזור להגדרות
            <ChevronRight className="h-4 w-4" />
          </Button>
          <Button
            variant="default"
            onClick={() =>
              navigate("/shopping-list", { state: { mealPlanId: id } })
            }
            className="rtl-button-icon"
          >
            רשימת קניות
            <ShoppingCart className="h-4 w-4" />
          </Button>
        </div>

        <h1 className="text-2xl font-semibold text-right">
          תוכנית הארוחות שלך
        </h1>

        <div className="space-y-6">
          {mealPlan.days.map((day: any) => (
            <Card key={day.dayNumber} className="p-6">
              <h2 className="text-xl font-semibold mb-6 text-right">
                יום {day.dayNumber}
              </h2>
              <div className="space-y-6">
                {Object.entries(day.meals).map(
                  ([type, meal]: [string, any], index: number, arr: any[]) => (
                    <div key={type}>
                      <div className="flex flex-col space-y-4">
                        <div className="flex flex-col">
                          <div className="text-right">
                            <h3 className="text-lg font-medium text-muted-foreground mb-2 text-right">
                              {
                                translations.mealPlanner.mealTypes[
                                  type as keyof typeof translations.mealPlanner.mealTypes
                                ]
                              }
                            </h3>
                            <h4 className="text-xl font-semibold mb-2 text-right">
                              {meal.name}
                            </h4>
                            <p className="text-sm text-muted-foreground mb-4 text-right">
                              {meal.description}
                            </p>
                            <div className="flex flex-row-reverse justify-start items-center gap-4 mb-4">
                              <div className="flex flex-row-reverse items-center gap-2 text-sm text-muted-foreground">
                                <Clock className="h-4 w-4" />
                                <span>{meal.cookingTime} דקות</span>
                              </div>
                              <RecipeDialog
                                meal={meal}
                                onToggleFavorite={() =>
                                  handleToggleFavorite(meal)
                                }
                                isFavorite={isFavorite(meal)}
                              />
                            </div>
                            <div className="flex justify-end">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() =>
                                  handleChangeMeal(day.dayNumber, type as "breakfast" | "lunch" | "dinner")
                                }
                                className="rtl-button-icon text-muted-foreground hover:text-primary"
                              >
                                החלף ארוחה
                                <RefreshCw className="h-4 w-4" />
                              </Button>
                            </div>
                          </div>
                        </div>
                      </div>
                      {index < arr.length - 1 && <Separator className="my-6" />}
                    </div>
                  ),
                )}
              </div>
            </Card>
          ))}
        </div>
      </div>

      {selectedMeal && (
        <ChangeMealDialog
          isOpen={!!selectedMeal}
          onClose={() => setSelectedMeal(null)}
          onGenerateNew={() => {
            setShowMealOptions(true);
          }}
          onImportFromUrl={handleImportFromUrl}
          onImportFromVideo={(url) => {
            // Implement video import logic
            console.log("Import from video:", url);
          }}
          onSelectFavorite={async (meal) => {
            try {
              if (!id || !selectedMeal) return;
              const updatedPlan = await updateMealInPlan(
                selectedMeal.dayNumber,
                selectedMeal.type,
                meal,
                id,
              );
              setMealPlan(updatedPlan);
              setSelectedMeal(null);
            } catch (error) {
              console.error("Failed to update meal with favorite:", error);
            }
          }}
        />
      )}

      {showMealOptions && selectedMeal && (
        <MealOptionsDialog
          isOpen={showMealOptions}
          onClose={() => setShowMealOptions(false)}
          options={mealOptions}
          onSelect={handleMealOptionSelect}
          loading={loadingOptions}
        />
      )}
    </div>
  );
}
